import React from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HistoricalTableKey, setCurrentPage, setItemsPerPage } from '../../redux/historicalSlice';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from '../../redux/store';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalRecords?: number;
    pageSizeOptions?: number[];
    showExportLink?: boolean;
    onExportCsv?: () => void;
    exportLinkText?: string;
    showImportLink?: boolean;
    onImportCsv?: () => void;
    importLinkText?: string;
    selectorType: HistoricalTableKey;
    onDownloadTemplate?: () => void;
    downloadTemplateText?: string;
    asTableRow?: boolean;
}

export default function AppTablePagination({
    totalPages,
    pageSize = 10,
    pageSizeOptions = [5, 10, 25, 50],
    totalRecords,
    showExportLink = false,
    onExportCsv,
    exportLinkText,
    showImportLink = false,
    onImportCsv,
    importLinkText,
    selectorType,
    onDownloadTemplate,
    downloadTemplateText,
    asTableRow = true
}: PaginationProps) {

    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();

    const currentPage = useSelector((state: RootState) =>
        state.historical[selectorType]?.currentPage
    );
    useSelector((state: RootState) => state.historical[selectorType]?.itemsPerPage);

    const handleChangePage = (newPage: number) => {
        dispatch(setCurrentPage({ table: selectorType, page: newPage }));
    }

    const handleChangePageSize = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPage = Number(event.target.value);
        dispatch(setItemsPerPage({ table: selectorType, size: newPage }));
        dispatch(setCurrentPage({ table: selectorType, page: 1 }));
    }
   
    const navButtonStyle: React.CSSProperties & { [key: string]: string } = {
        marginRight: "5px",
        borderRadius: "5px",
        padding: "0.2rem 0.4rem",
        "--bs-btn-hover-bg": "#f2f2f2",
        "--bs-btn-hover-color": "#000000",
        // "--bs-btn-hover-border-color": "#d6d6d6",
    };

    const infoButtonStyle: React.CSSProperties & { [key: string]: string } = {
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: "inherit",
        padding: "0.2rem 0.5rem",
    };

    const effectivePageSizeOptions = totalRecords == null
        ? pageSizeOptions
        : pageSizeOptions.filter(option => option <= totalRecords || option === pageSize);

    const pageSizeOptionsToRender = effectivePageSizeOptions.length > 0
        ? effectivePageSizeOptions
        : [pageSize];

    const content = (
        <div className="d-flex justify-content-center position-relative">
            <ButtonGroup className='ms-5'>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    style={navButtonStyle}
                    onClick={() => handleChangePage(1)}
                    disabled={currentPage == 1}
                >
                    <i className="bi bi-chevron-double-left"></i>
                </Button>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    style={navButtonStyle}
                    onClick={() => handleChangePage(currentPage - 1)}
                    disabled={currentPage <= 1}
                >
                    <i className="bi bi-chevron-left"></i>
                </Button>
                <Button
                    size="sm"
                    variant="light"
                    style={infoButtonStyle}
                    disabled
                >
                    {currentPage} {t("common:pagination.paginationCommentTotals")} {totalPages}
                </Button>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    style={navButtonStyle}
                    onClick={() => handleChangePage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                >
                    <i className="bi bi-chevron-right"></i>
                </Button>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    style={navButtonStyle}
                    onClick={() => handleChangePage(totalPages)}
                    disabled={currentPage == totalPages}
                >
                    <i className="bi bi-chevron-double-right"></i>
                </Button>
            </ButtonGroup>
            <div className="d-flex align-items-center ml-3">
                <select
                    id="itemsPerPage"
                    className="form-select form-select-sm"
                    style={{ width: "80px" }}
                    onChange={handleChangePageSize}
                    value={pageSize}
                >
                    {pageSizeOptionsToRender.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
                <label htmlFor="itemsPerPage" className="ml-2 mb-0">
                    {t("common:pagination.paginationItemsPerPage")}
                </label>
            </div>
            {(showExportLink || showImportLink || onDownloadTemplate) && (
                <div className="d-flex justify-content-end align-items-center gap-3 position-absolute end-0">
                    {onDownloadTemplate && (
                        <a href="#" onClick={(e) => { e.preventDefault(); onDownloadTemplate?.(); }}>
                            <p className="text-secondary mb-0">
                                {downloadTemplateText || t("common:pagination.downloadTemplate")}
                            </p>
                        </a>
                    )}
                    {showImportLink && (
                        <a href="#" onClick={(e) => { e.preventDefault(); onImportCsv?.(); }}>
                            <p className="text-secondary mb-0">
                                {importLinkText || t("common:pagination.linkImportCSV")}
                            </p>
                        </a>
                    )}
                    {showExportLink && (
                        <a href="#" onClick={(e) => { e.preventDefault(); onExportCsv?.(); }}>
                            <p className="text-secondary mb-0">
                                {exportLinkText || t("common:pagination.linkExportCSV")}
                            </p>
                        </a>
                    )}
                </div>
            )}
        </div>
    );

    if (asTableRow) {
        return (
            <tr>
                <td colSpan={12} style={{ border: "none" }}>
                    {content}
                </td>
            </tr>
        );
    }

    return <div className="mt-2">{content}</div>;
};
